if (!customElements.get('sticky-image-cards')) {
  customElements.define(
    'sticky-image-cards',
    class StickyImageCards extends HTMLElement {
      constructor() {
        super();

        this.isDesktop = Boolean(window.theme.getWindowWidth() >= window.theme.sizes.large);
        this.isMobile = window.theme.isMobile();
        this.orientation = window.theme.getScreenOrientation();

        this.onScrollCallback = () => this.onScroll();
        this.onResizeCallback = () => this.onResize();
      }

      connectedCallback() {
        this.container = this.closest('[data-section-type="sticky-image-cards"]');
        this.body = this.querySelector('[data-sticky-card-body]');
        this.wrapper = this.querySelectorAll('[data-sticky-card-wrapper]');
        this.content = this.querySelector('[data-sticky-card-content]');
        this.aside = this.querySelector('[data-sticky-card-aside]');
        this.media = this.querySelectorAll('[data-sticky-card-media]');
        this.textInContent = this.content?.querySelectorAll('[data-sticky-card-text]');
        this.layoutMobile = this.getAttribute('data-sticky-card-layout-mobile');

        if (this.media.length === 0) return;

        // Safari fix: Wait for CSS/fonts to load before calculating heights
        const performInitialCalculation = () => {
          if (!this.isConnected) return;
          this.calculateHeights();
          this.onScroll();
        };

        const scheduleCalculation = () => {
          requestAnimationFrame(() => {
            requestAnimationFrame(performInitialCalculation);
          });
        };

        // Wait for document ready state
        if (document.readyState === 'complete') {
          // Document already loaded - calculate immediately
          scheduleCalculation();
        } else {
          // Wait for load event (includes CSS, fonts, images)
          window.addEventListener('load', scheduleCalculation, {once: true});
        }

        document.addEventListener('theme:scroll', this.onScrollCallback);
        document.addEventListener('theme:resize', this.onResizeCallback);
        if (window.visualViewport) {
          window.visualViewport.addEventListener('resize', this.onResizeCallback);
        }
      }

      onScroll() {
        if (!this.isEligible()) return;

        this.detectStickyPositions();
        if (this.isMobile) {
          this.updateSpillover();
        }
      }

      isEligible() {
        const elementOffsetTopPoint = Math.round(this.container.getBoundingClientRect().top + window.scrollY);
        const elementOffsetBottomPoint = elementOffsetTopPoint + this.container.offsetHeight;
        const isBottomOfElementPassed = elementOffsetBottomPoint < Math.round(window.scrollY);
        const isTopOfElementReached = elementOffsetTopPoint < Math.round(window.scrollY + window.innerHeight);
        const isInView = isTopOfElementReached && !isBottomOfElementPassed;

        return isInView;
      }

      getViewportHeight() {
        if (window.visualViewport?.height) {
          return window.visualViewport.height;
        }
        return window.innerHeight;
      }

      detectStickyPositions() {
        const parentOffset = Math.round(this.body.getBoundingClientRect().top - this.headerHeight - this.wrapperTop);

        [...this.wrapper].forEach((item, i) => {
          const distance = Math.floor(item.getBoundingClientRect().top - this.headerHeight);

          if (this.isMobile) {
            if (i !== this.lastIndex) {
              const nextOneDistance = Math.floor(this.wrapper[i + 1].getBoundingClientRect().top - this.headerHeight);
              item.classList.toggle('has-passed-sticky', nextOneDistance <= this.stickyTop);
            }

            this.container.classList.toggle(`checkpoint-${i + 1}`, parentOffset <= this.cumulativeHeights[i] * -1);
          }

          item.classList.toggle('is-stuck-up', distance <= this.stickyTop);
        });
      }

      calculateHeights() {
        let {stickyHeaderHeight} = window.theme.readHeights();
        this.headerHeight = stickyHeaderHeight || 0;
        this.spacingTop = parseInt(window.getComputedStyle(this.container).getPropertyValue('--padding')) || 20;
        this.wrapperTop = parseInt(window.getComputedStyle(this.wrapper[0]).getPropertyValue('top')) || 0;
        this.stickyTop = Math.floor(this.wrapperTop - this.headerHeight);
        this.gap = parseInt(window.getComputedStyle(this.aside).getPropertyValue('gap')) || 0;
        this.viewportHeight = this.getViewportHeight();
        this.lastIndex = [...this.wrapper].length - 1;
        this.cumulativeHeights = [];

        if (this.isMobile) {
          this.wrapperTop = this.spacingTop;
          this.stickyTop = this.spacingTop;
        }

        if (!this.textInContent) return;

        // Minimum height variables
        if (this.isDesktop && this.textInContent.length > 0) {
          this.textInContent.forEach((element, i) => {
            element.style.setProperty('--row-height', 'auto');
            this.wrapper[i].style.setProperty('--row-height', 'auto');

            const elementHeight = element.offsetHeight;
            const mediaHeight = this.media[i].offsetHeight;
            const maxHeight = Math.max(elementHeight, mediaHeight);

            element.style.setProperty('--row-height', `${maxHeight}px`);
            this.wrapper[i].style.setProperty('--row-height', `${maxHeight}px`);
          });
        }

        // Handle excessive content heights
        requestAnimationFrame(() => {
          let runningSum = 0;
          this.cumulativeHeights = [];

          [...this.wrapper].forEach((item, i) => {
            item.style.setProperty('--item-height', 'auto');
            item.style.setProperty('--item-double-height', 'auto');

            const itemHeight = item.offsetHeight;

            this.cumulativeHeights[i] = runningSum;
            runningSum += itemHeight;

            // Preserve measured heights for potential spillover adjustments
            item.dataset.measuredHeight = itemHeight;
          });

          this.updateSpillover();
        });
      }

      updateSpillover() {
        if (!this.isMobile || !this.wrapper?.length) return;

        const viewportHeight = this.getViewportHeight();
        const availableHeight = viewportHeight - this.headerHeight - this.wrapperTop;

        [...this.wrapper].forEach((item) => {
          const itemHeight = Number(item.dataset.measuredHeight) || item.offsetHeight;
          const isExceeding = itemHeight > availableHeight;

          item.classList.toggle('spillover', isExceeding);

          if (isExceeding) {
            item.style.setProperty('--item-height', `${itemHeight}px`);
            item.style.setProperty('--item-double-height', `${itemHeight * 2}px`);
          } else {
            item.style.setProperty('--item-height', 'auto');
            item.style.setProperty('--item-double-height', 'auto');
            item.dataset.measuredHeight = '';
          }
        });
      }

      onResize() {
        requestAnimationFrame(() => {
          this.isDesktop = Boolean(window.theme.getWindowWidth() >= window.theme.sizes.large);
          this.isMobile = window.theme.isMobile();
          const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
          let isEligible = this.isDesktop || (!isIOS && this.isMobile);

          // Prevent updating of heights and props on iOS devices, where the `resize` event is triggered every time one is scrolling up and down the page
          if (this.orientation !== window.theme.getScreenOrientation() && isIOS) {
            isEligible = true;
            this.orientation = window.theme.getScreenOrientation();
          }

          if (!isEligible) return;
          this.calculateHeights();
        });
      }

      /**
       * Event callback for Theme Editor `shopify:section:unload` event
       */
      disconnectedCallback() {
        if (window.visualViewport) {
          window.visualViewport.removeEventListener('resize', this.onResizeCallback);
        }
        document.removeEventListener('theme:scroll', this.onScrollCallback);
        document.removeEventListener('theme:resize', this.onResizeCallback);
      }
    }
  );
}
